import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Pill, Plus, CheckCircle2, Clock as ClockIcon, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Medications() {
  const { user } = useAuth();
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ medication_name: '', dosage: '', frequency: '', instructions: '' });
  const [selectedClient, setSelectedClient] = useState('');
  const [clients, setClients] = useState([]);
  const [logDialog, setLogDialog] = useState(false);
  const [selectedMed, setSelectedMed] = useState(null);
  const [logNotes, setLogNotes] = useState('');
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => {
    fetchMeds();
    if (isAdmin || user?.role === 'caregiver') fetchClients();
  }, [user]);

  const fetchMeds = async () => {
    try {
      const url = selectedClient ? `${API_URL}/api/medications?client_id=${selectedClient}` : `${API_URL}/api/medications`;
      const { data } = await axios.get(url, { withCredentials: true });
      setMeds(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/by-org?role=client`, { withCredentials: true });
      setClients(data);
    } catch (err) { console.error(err); }
  };

  const handleAdd = async () => {
    if (!form.medication_name) return toast.error('Medication name required');
    try {
      await axios.post(`${API_URL}/api/medications`, { ...form, client_id: selectedClient }, { withCredentials: true });
      toast.success('Medication added');
      setDialogOpen(false);
      setForm({ medication_name: '', dosage: '', frequency: '', instructions: '' });
      fetchMeds();
    } catch (err) { toast.error('Failed'); }
  };

  const handleLog = async () => {
    if (!selectedMed) return;
    try {
      await axios.post(`${API_URL}/api/medications/${selectedMed.id}/log`, { status: 'given', notes: logNotes }, { withCredentials: true });
      toast.success('Medication logged');
      setLogDialog(false);
      setSelectedMed(null);
      setLogNotes('');
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="medications-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <Pill className="w-7 h-7 inline mr-3 text-[#A8E6CF]" />Medications
            </h1>
            <p className="text-muted-foreground mt-1">Track and log medication administration</p>
          </div>
          {(isAdmin || user?.role === 'caregiver') && (
            <Button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="add-medication-btn"><Plus className="w-4 h-4 mr-2" />Add Medication</Button>
          )}
        </div>

        {meds.length === 0 ? (
          <div className="text-center py-16"><Pill className="w-16 h-16 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No medications recorded</p></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {meds.map(m => (
              <Card key={m.id} className="glass-card border-border" data-testid={`medication-${m.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-foreground font-medium">{m.medication_name}</h3>
                      {m.dosage && <p className="text-sm text-[#00A6A0]">{m.dosage}</p>}
                    </div>
                    <Pill className="w-5 h-5 text-[#A8E6CF]" />
                  </div>
                  {m.frequency && <p className="text-xs text-muted-foreground">Frequency: {m.frequency}</p>}
                  {m.instructions && <p className="text-xs text-muted-foreground mt-1">{m.instructions}</p>}
                  {user?.role === 'caregiver' && (
                    <Button size="sm" className="mt-3 w-full bg-[#A8E6CF] hover:bg-[#6B8B67] text-white"
                      onClick={() => { setSelectedMed(m); setLogDialog(true); }} data-testid={`log-med-${m.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Log Administration
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="glass-card border-border max-w-md">
            <DialogHeader><DialogTitle className="text-foreground">Add Medication</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {clients.length > 0 && (
                <div><label className="text-xs text-muted-foreground">Client</label>
                  <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div><label className="text-xs text-muted-foreground">Medication Name</label>
                <Input value={form.medication_name} onChange={e => setForm(p => ({...p, medication_name: e.target.value}))} className="bg-muted border-border text-foreground" data-testid="med-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground">Dosage</label>
                  <Input value={form.dosage} onChange={e => setForm(p => ({...p, dosage: e.target.value}))} placeholder="e.g., 500mg" className="bg-muted border-border text-foreground" /></div>
                <div><label className="text-xs text-muted-foreground">Frequency</label>
                  <Input value={form.frequency} onChange={e => setForm(p => ({...p, frequency: e.target.value}))} placeholder="e.g., Twice daily" className="bg-muted border-border text-foreground" /></div>
              </div>
              <div><label className="text-xs text-muted-foreground">Instructions</label>
                <textarea value={form.instructions} onChange={e => setForm(p => ({...p, instructions: e.target.value}))} placeholder="Special instructions..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] focus:ring-1 focus:ring-[#00A6A0] focus:outline-none" /></div>
              <Button className="w-full btn-primary" onClick={handleAdd} data-testid="save-med-btn">Save Medication</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={logDialog} onOpenChange={setLogDialog}>
          <DialogContent className="glass-card border-border max-w-sm">
            <DialogHeader><DialogTitle className="text-foreground">Log Administration</DialogTitle></DialogHeader>
            {selectedMed && (
              <div className="space-y-3">
                <p className="text-[#00A6A0] font-medium">{selectedMed.medication_name} - {selectedMed.dosage}</p>
                <div><label className="text-xs text-muted-foreground">Notes</label>
                  <textarea value={logNotes} onChange={e => setLogNotes(e.target.value)} placeholder="Any notes..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] focus:ring-1 focus:ring-[#00A6A0] focus:outline-none" /></div>
                <Button className="w-full btn-primary" onClick={handleLog} data-testid="confirm-log-med-btn">Confirm Administration</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
